/* Specimen theme — Made by Gray. No dependencies. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const theme = window.theme || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll lock + overlay ---------- */
  const lockers = new Set();
  const lock = (key) => { lockers.add(key); document.body.classList.add('scroll-lock'); };
  const unlock = (key) => { lockers.delete(key); if (!lockers.size) document.body.classList.remove('scroll-lock'); };

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(html) {
    const el = $('[data-toast]');
    if (!el) return;
    el.innerHTML = html;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 350);
    }, 3200);
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root = document) {
    const items = $$('[data-reveal]:not(.is-revealed)', root);
    if (!document.documentElement.classList.contains('has-reveal') || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting);
      visible.forEach((entry, i) => {
        entry.target.style.setProperty('--reveal-delay', `${Math.min(i, 6) * 0.06}s`);
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Header: mega menu ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const items = $$('[data-mega-item]', header);
    let closeTimer;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        const t = $('[data-mega-trigger]', item);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      if (!except) header.classList.remove('menu-open');
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      closeAll(item);
      item.classList.add('is-open');
      $('[data-mega-trigger]', item).setAttribute('aria-expanded', 'true');
      header.classList.add('menu-open');
    };

    items.forEach((item) => {
      const trigger = $('[data-mega-trigger]', item);
      item.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) open(item); });
      item.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => closeAll(), 160); });
      trigger.addEventListener('click', (e) => {
        if (item.classList.contains('is-open') && !window.matchMedia('(hover: hover)').matches) return;
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(item); }
      });
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); open(item); const first = $('.mega a', item); if (first) first.focus(); }
      });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) closeAll(); });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openItem = items.find((i) => i.classList.contains('is-open'));
      if (openItem) { closeAll(); $('[data-mega-trigger]', openItem).focus(); }
    });
    document.addEventListener('click', (e) => { if (!header.contains(e.target)) closeAll(); });
  }

  /* ---------- Mobile menu ---------- */
  function initDrawerMenu() {
    const drawer = $('[data-drawer-menu]');
    if (!drawer) return;
    const openers = $$('[data-menu-open]');
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      unlock('menu');
    };
    openers.forEach((btn) => btn.addEventListener('click', () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      btn.setAttribute('aria-expanded', 'true');
      lock('menu');
      const c = $('[data-menu-close]', drawer); if (c) c.focus();
    }));
    $$('[data-menu-close]', drawer).forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  }

  /* ---------- Search panel + predictive search ---------- */
  function initSearch() {
    const panel = $('[data-search-panel]');
    if (!panel) return;
    const input = $('input[type="search"]', panel);
    const results = $('[data-search-results]', panel);
    let controller;
    let timer;

    const open = () => {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      lock('search');
      setTimeout(() => input && input.focus(), 50);
    };
    const close = () => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      unlock('search');
    };
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    $$('[data-search-close]', panel).forEach((b) => b.addEventListener('click', close));
    panel.addEventListener('click', (e) => { if (e.target === panel) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
      if (e.key === '/' && !panel.classList.contains('is-open') && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) { e.preventDefault(); open(); }
    });

    const money = (value) => {
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      if (n === 0) return `<span class="price price--free"><span class="price__chip">${theme.strings.free || 'Free'}</span></span>`;
      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(n); } catch (err) { return `$${n.toFixed(2)}`; }
    };
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    if (!input || !results) return;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const url = `${theme.routes.predictiveSearch || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: controller.signal });
          const data = await res.json();
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            results.innerHTML = `<p class="search-result"><span></span><span class="muted">${esc(theme.strings.noResults || 'No matches')}</span><span></span></p>`;
            return;
          }
          results.innerHTML = products.map((p) => {
            const img = p.featured_image && p.featured_image.url ? `<img src="${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=120" alt="" loading="lazy">` : '';
            return `<a class="search-result" href="${p.url}"><span class="search-result__img">${img}</span><span><span class="search-result__title">${esc(p.title)}</span><br><span class="label">${esc(p.type || '')}</span></span><span>${money(p.price)}</span></a>`;
          }).join('');
        } catch (err) { if (err.name !== 'AbortError') results.innerHTML = ''; }
      }, 180);
    });
  }

  /* ---------- Cart ---------- */
  const Cart = {
    drawer: null,
    overlay: null,
    sectionId: 'cart-drawer',

    init() {
      this.drawer = $('[data-cart-drawer]');
      this.overlay = $('[data-cart-overlay]');
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        this.add(form);
      });
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && this.drawer && theme.cartType === 'drawer') { e.preventDefault(); this.open(); return; }
        if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) { e.preventDefault(); this.change(Number(remove.dataset.line), 0, remove.closest('.line')); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) this.close(); });
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      lock('cart');
      const c = $('[data-cart-close]', this.drawer); if (c) setTimeout(() => c.focus(), 60);
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-open');
      unlock('cart');
    },

    sections() {
      const ids = [];
      if (this.drawer) ids.push(this.sectionId);
      if ($('[data-cart-page]')) ids.push($('[data-cart-page]').dataset.sectionId);
      return ids;
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === this.sectionId && this.drawer) {
          const fresh = $('[data-cart-drawer-inner]', doc);
          const target = $('[data-cart-drawer-inner]', this.drawer);
          if (fresh && target) target.replaceWith(fresh);
          const count = $('[data-cart-drawer]', doc);
          if (count) this.setCount(Number(count.dataset.count));
        } else {
          const fresh = $('[data-cart-page]', doc);
          const target = $('[data-cart-page]');
          if (fresh && target) target.replaceWith(fresh);
          if (fresh) this.setCount(Number(fresh.dataset.count));
        }
      });
    },

    setCount(n) {
      if (Number.isNaN(n)) return;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = false;
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
        setTimeout(() => el.classList.remove('is-bumped'), 450);
      });
    },

    async add(form) {
      const btn = $('[type="submit"]', form);
      const label = btn ? btn.innerHTML : '';
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
      const body = new FormData(form);
      const sections = this.sections();
      if (sections.length) { body.append('sections', sections.join(',')); body.append('sections_url', window.location.pathname); }
      try {
        const res = await fetch(`${theme.routes.cartAdd || '/cart/add'}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
        if (form.dataset.directCheckout === 'true') { window.location.href = '/checkout'; return; }
        this.render(data.sections);
        if (btn) {
          btn.classList.add('is-added');
          btn.innerHTML = `<span>${theme.strings.added || 'Added'}</span>`;
          setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = label; }, 1600);
        }
        if (this.drawer && theme.cartType === 'drawer') {
          this.open();
        } else {
          if (!sections.length) this.refreshCount();
          toast(`${theme.strings.added || 'Added'} — <a href="${theme.routes.cart}" style="text-decoration:underline">${theme.strings.viewCart || 'View cart'}</a>`);
        }
      } catch (err) {
        toast(err.message || theme.strings.error);
        if (btn) btn.innerHTML = label;
      } finally {
        if (btn) { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
      }
    },

    async change(line, quantity, row) {
      if (row) row.classList.add('is-removing');
      try {
        const res = await fetch(`${theme.routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || theme.strings.error);
        this.render(data.sections);
        if (!this.sections().length) this.setCount(data.item_count);
      } catch (err) {
        if (row) row.classList.remove('is-removing');
        toast(err.message || theme.strings.error);
      }
    },

    async refreshCount() {
      try { const r = await fetch('/cart.js'); const c = await r.json(); this.setCount(c.item_count); } catch (e) { /* noop */ }
    }
  };

  /* ---------- Hero specimen rotator ---------- */
  function initSpecimens() {
    $$('[data-specimen]').forEach((root) => {
      const slides = $$('[data-slide]', root);
      const caption = $('[data-specimen-caption]', root);
      const counter = $('[data-specimen-counter]', root);
      const bar = $('[data-specimen-progress]', root);
      const interval = Number(root.dataset.interval || 4) * 1000;
      if (slides.length < 2) return;
      let i = 0;
      let timer;
      const pad = (n) => String(n).padStart(2, '0');

      const go = (next) => {
        slides[i].classList.remove('is-active');
        i = (next + slides.length) % slides.length;
        slides[i].classList.add('is-active');
        if (caption) caption.textContent = slides[i].dataset.caption || '';
        if (counter) counter.textContent = `${pad(i + 1)} / ${pad(slides.length)}`;
        if (bar) { bar.classList.remove('is-running'); void bar.offsetWidth; bar.classList.add('is-running'); }
      };
      const play = () => { if (reduceMotion) return; stop(); timer = setInterval(() => go(i + 1), interval); if (bar) { bar.classList.remove('is-running'); void bar.offsetWidth; bar.classList.add('is-running'); } };
      const stop = () => clearInterval(timer);

      $$('[data-specimen-prev]', root).forEach((b) => b.addEventListener('click', () => { go(i - 1); play(); }));
      $$('[data-specimen-next]', root).forEach((b) => b.addEventListener('click', () => { go(i + 1); play(); }));
      root.addEventListener('mouseenter', () => { stop(); if (bar) bar.style.animationPlayState = 'paused'; });
      root.addEventListener('mouseleave', () => { if (bar) bar.style.animationPlayState = ''; play(); });
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
      play();
    });
  }

  /* ---------- Product index (hover to preview) ---------- */
  function initProductIndex() {
    $$('[data-pindex]').forEach((root) => {
      const items = $$('[data-pindex-item]', root);
      const imgs = $$('[data-pindex-img]', root);
      const details = $$('[data-pindex-detail]', root);
      const num = $('[data-pindex-num]', root);
      const set = (idx) => {
        items.forEach((el, n) => el.classList.toggle('is-active', n === idx));
        imgs.forEach((el, n) => el.classList.toggle('is-active', n === idx));
        details.forEach((el, n) => el.classList.toggle('is-active', n === idx));
        if (num) num.textContent = String(idx + 1).padStart(2, '0');
      };
      items.forEach((el, n) => {
        el.addEventListener('mouseenter', () => set(n));
        el.addEventListener('focusin', () => set(n));
      });
    });
  }

  /* ---------- Collection: grid/list toggle + cursor preview ---------- */
  function initCollection() {
    const root = $('[data-collection]');
    if (!root) return;
    const grid = $('[data-view="grid"]', root);
    const list = $('[data-view="list"]', root);
    const buttons = $$('[data-view-btn]', root);
    const key = 'specimen:view';
    const setView = (v) => {
      if (grid) grid.hidden = v !== 'grid';
      if (list) list.hidden = v !== 'list';
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewBtn === v)));
      try { localStorage.setItem(key, v); } catch (e) { /* storage unavailable */ }
      if (v === 'grid' && grid) initReveal(grid);
    };
    let saved = root.dataset.defaultView || 'grid';
    try { saved = localStorage.getItem(key) || saved; } catch (e) { /* storage unavailable */ }
    setView(saved);
    buttons.forEach((b) => b.addEventListener('click', () => setView(b.dataset.viewBtn)));

    const sort = $('[data-sort]', root);
    if (sort) sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
    });

    const preview = $('[data-cursor-preview]');
    if (!preview || !list || !window.matchMedia('(hover: hover)').matches) return;
    const img = $('img', preview);
    let x = 0, y = 0, cx = 0, cy = 0, raf;
    const loop = () => {
      cx += (x - cx) * 0.18; cy += (y - cy) * 0.18;
      preview.style.transform = `translate3d(${cx + 28}px, ${cy - 120}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    $$('[data-preview-src]', list).forEach((row) => {
      row.addEventListener('mouseenter', (e) => {
        if (!row.dataset.previewSrc) return;
        img.src = row.dataset.previewSrc;
        x = cx = e.clientX; y = cy = e.clientY;
        preview.classList.add('is-visible');
        cancelAnimationFrame(raf); loop();
      });
      row.addEventListener('mousemove', (e) => { x = e.clientX; y = e.clientY; });
      row.addEventListener('mouseleave', () => { preview.classList.remove('is-visible'); cancelAnimationFrame(raf); });
    });
  }

  /* ---------- Product media counter (mobile slider) ---------- */
  function initProductMedia() {
    $$('[data-product-gallery]').forEach((root) => {
      const slides = $$('[data-slide]', root);
      const thumbs = $$('[data-thumb]', root);
      const count = $('[data-media-count]', root);
      if (slides.length < 2) return;
      const pad = (n) => String(n).padStart(2, '0');
      let i = 0;
      const go = (next) => {
        i = (next + slides.length) % slides.length;
        slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
        thumbs.forEach((t, n) => {
          const on = n === i;
          t.classList.toggle('is-active', on);
          if (on) t.setAttribute('aria-selected', 'true'); else t.removeAttribute('aria-selected');
        });
        if (count) count.textContent = `${pad(i + 1)} / ${pad(slides.length)}`;
      };
      thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));
      $$('[data-gallery-prev]', root).forEach((b) => b.addEventListener('click', () => go(i - 1)));
      $$('[data-gallery-next]', root).forEach((b) => b.addEventListener('click', () => go(i + 1)));
      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(i - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); go(i + 1); }
      });
    });
  }

  /* ---------- Product recommendations ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      const url = el.dataset.url;
      if (!url) return;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.querySelector('.card')) {
          el.innerHTML = fresh.innerHTML;
          el.hidden = false;
          initReveal(el);
        }
      } catch (e) { /* leave hidden */ }
    });
  }

  /* ---------- Fit oversized text to its container ---------- */
  function fitText() {
    $$('[data-fit-text]').forEach((el) => {
      const span = $('span', el);
      if (!span) return;
      el.style.fontSize = '100px';
      const cs = getComputedStyle(el);
      const avail = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const w = span.getBoundingClientRect().width;
      if (w > 0) el.style.fontSize = `${Math.floor((100 * avail / w) * 100) / 100}px`;
    });
  }

  /* ---------- Boot ---------- */
  const boot = () => {
    initReveal();
    initHeader();
    initDrawerMenu();
    initSearch();
    Cart.init();
    initSpecimens();
    initProductIndex();
    initCollection();
    initProductMedia();
    initRecommendations();
    fitText();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitText);
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(fitText, 120); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-run pieces when sections change in the theme editor.
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initSpecimens();
    initProductIndex();
    initCollection();
    initProductMedia();
  });

  window.SpecimenCart = Cart;
})();
